/**
 * BILLING-CURRENCY MICRO-UNITS — the canonical internal unit for tenant money.
 *
 * Design of record: docs/plans/2026-08-14_BILLING_CURRENCY_NATIVE_CREDITS.md §1.
 *
 * ── Why micro and not minor ─────────────────────────────────────────────
 *
 * A tenant's pool, caps and ledger are denominated in the DEPLOYMENT's billing
 * currency (`billing_settings.billing_currency`), not USD. The obvious unit is
 * the currency's own minor unit — sen, cents — and it is too coarse by three
 * orders of magnitude for a single LLM turn: a US$0.0001 call at a credit rate
 * of 550 minor/USD is 0.055 minor units, which rounds to ZERO. A plan's whole
 * pool could be consumed by rows that each record nothing.
 *
 * So the unit is 10^-6 of the MAJOR unit, mirroring the USD-microdollar
 * convention already used for provider cost. The same turn is 550 µMYR, exact.
 *
 * ── Representation contract (§1.2) ──────────────────────────────────────
 *
 * PostgreSQL stores `bigint`. The application uses JavaScript `number` with
 * `Number.MAX_SAFE_INTEGER` as an ENFORCED ceiling — not native `bigint`,
 * because all 43 existing `bigint` columns declare `mode: 'number'` and
 * switching them is a second refactor the size of the first.
 *
 * `Number.isSafeInteger` is therefore the guard, applied at every boundary:
 * repository reads, `SUM()` aggregates, writes, DTO boundaries, and the
 * intermediate product below. A value outside the range THROWS — it is a
 * corrupt money figure, not something to clamp or round.
 *
 * BigInt appears inside `usdMicroToBillingMicro` purely as an implementation
 * detail: it is the cheapest way to make the divide provably exact. It never
 * crosses this module's boundary.
 */
import { currencyMinorUnitsPerMajor, isSupportedBillingCurrency } from './currency.js'

/** Micro-units in one major currency unit. 10^-6 of the major unit. */
export const BILLING_MICRO_PER_MAJOR = 1_000_000

/**
 * Ceiling on a SINGLE operator credit adjustment, in billing micro — one
 * million major units (RM1,000,000).
 *
 * A sanity bound on a hand-typed figure, not a business limit: it is the
 * difference between a fat-fingered extra digit being refused and it being
 * granted. Deliberately its OWN constant rather than borrowing promo's
 * `CREDIT_AMOUNT_BILLING_MICRO_MAX`, which happens to be the same number in a
 * different unit — reusing it would be a units pun that survives review.
 */
export const CREDIT_ADJUST_BILLING_MICRO_MAX = 1_000_000_000_000

/** `Number.MAX_SAFE_INTEGER` as a BigInt, for exact comparisons. */
const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER)

/**
 * Thrown when a monetary value falls outside what the application may
 * represent. Matched by `name`, like the other money-path errors, so a route
 * can map it without importing the class.
 */
export class MonetaryRangeError extends Error {
  override readonly name = 'MonetaryRangeError'
  constructor(what: string, detail: string) {
    super(`${what} is not a representable monetary amount: ${detail}`)
  }
}

/**
 * The guard §1.2 mandates. Use at repository reads, aggregate results, writes,
 * and DTO boundaries — anywhere a `bigint` column becomes a JavaScript number.
 *
 * PostgreSQL `bigint` holds values this forbids, and `mode: 'number'` would
 * silently round them, so an unguarded read is how a wrong charge enters
 * without an error.
 */
export function assertSafeMonetary(value: number, what = 'amount'): number {
  if (!Number.isSafeInteger(value)) {
    throw new MonetaryRangeError(what, `${value} is not a safe integer`)
  }
  return value
}

/**
 * Refuse a currency this deployment cannot charge in.
 *
 * `currencyMinorUnitExponent` falls back to **2** for any code `Intl` cannot
 * resolve. That is the right guess for DISPLAY, which is where it came from,
 * and it is dangerous here: a mis-stamped or empty `billing_currency` reaching
 * the charge path on a JPY deployment would divide by 100 instead of 1 — a
 * silent **100x undercharge**, with no error anywhere.
 *
 * So the charge boundary validates instead of guessing. Every other bad input
 * to this module throws; an unsupported currency is not different.
 */
export function assertSupportedCurrency(currency: string, what = 'currency'): string {
  if (!isSupportedBillingCurrency(currency)) {
    throw new MonetaryRangeError(
      what,
      `"${currency}" is not a supported billing currency — refusing to guess its minor-unit exponent`,
    )
  }
  return currency
}

/** As above, and additionally refuses negatives. Costs and balances are never negative. */
export function assertNonNegativeMonetary(value: number, what = 'amount'): number {
  assertSafeMonetary(value, what)
  if (value < 0) throw new MonetaryRangeError(what, `${value} is negative`)
  return value
}

/**
 * A payment amount in the currency's MINOR units → billing micro-units.
 *
 * Exact and RATE-FREE: this is a unit change within one currency, not a
 * conversion between two. It is the boundary used when a credit pack grants
 * its own face value (§8) — the pack's price IS the credit, so no rate is
 * consulted and none is stored.
 *
 * The multiplier is integral for every ISO exponent 0–3 (and up to 6), but the
 * check is explicit: a currency whose exponent made it fractional would
 * silently round a purchased face value, so it is refused instead.
 */
export function currencyMinorToBillingMicro(amountMinor: number, currency: string): number {
  assertNonNegativeMonetary(amountMinor, 'amountMinor')
  assertSupportedCurrency(currency)
  const minorPerMajor = currencyMinorUnitsPerMajor(currency)
  const multiplier = BILLING_MICRO_PER_MAJOR / minorPerMajor
  if (!Number.isInteger(multiplier)) {
    throw new MonetaryRangeError(
      'currency',
      `${currency} has ${minorPerMajor} minor units per major, which does not divide ${BILLING_MICRO_PER_MAJOR} micro-units exactly`,
    )
  }
  const product = BigInt(amountMinor) * BigInt(multiplier)
  if (product > MAX_SAFE) {
    throw new MonetaryRangeError('billing micro amount', `${product} exceeds Number.MAX_SAFE_INTEGER`)
  }
  return Number(product)
}

/**
 * The charge conversion — §2's option (d) formula.
 *
 *   billing_micro = round_half_up( usd_micro × rate / minorUnitsPerMajor )
 *
 * `usdMicro` is the **resale** cost (what the customer is charged in USD, after
 * `model_pricing` / `feature_pricing`), NOT the provider cost. The credit rate
 * carries the deployment's BASE margin, so a resale-price override composes
 * multiplicatively with it. That compounding is deliberate — see §2.
 *
 * Rounding is half-up, per row, at write. Any non-integer effective rate
 * produces a fractional micro at the smallest turn, so a policy is mandatory;
 * the error is bounded at ≤0.5 µ per row. A remainder accumulator was rejected:
 * it adds mutable per-tenant state to the hot enqueue path for an error term
 * three orders of magnitude below a sen, and it makes a single row's charge
 * unreproducible from its own inputs.
 *
 * Every row that stores the result also stores `usdMicro`, the rate and the
 * currency, so the charge recomputes from the row alone without consulting
 * mutable pricing configuration.
 */
export function usdMicroToBillingMicro(
  usdMicro: number,
  rateMinorPerUsd: number,
  currency: string,
): number {
  assertNonNegativeMonetary(usdMicro, 'usdMicro')
  assertNonNegativeMonetary(rateMinorPerUsd, 'rateMinorPerUsd')
  assertSupportedCurrency(currency)

  const divisor = BigInt(currencyMinorUnitsPerMajor(currency))

  // Exact in BigInt, so the intermediate product is checked against the ceiling
  // BEFORE any lossy double arithmetic can occur. Checking a double product
  // would already be checking a rounded value.
  const numerator = BigInt(usdMicro) * BigInt(rateMinorPerUsd)
  if (numerator > MAX_SAFE) {
    throw new MonetaryRangeError(
      'intermediate product',
      `${usdMicro} × ${rateMinorPerUsd} = ${numerator} exceeds Number.MAX_SAFE_INTEGER`,
    )
  }

  // Half-up via quotient and remainder. `2r >= d` avoids ever forming `d/2`,
  // which is fractional for a zero-decimal currency (JPY: d = 1).
  let quotient = numerator / divisor
  const remainder = numerator % divisor
  if (remainder * 2n >= divisor) quotient += 1n

  if (quotient > MAX_SAFE) {
    throw new MonetaryRangeError('billing micro amount', `${quotient} exceeds Number.MAX_SAFE_INTEGER`)
  }
  return Number(quotient)
}

/**
 * Billing micro-units → the currency's minor units, for a payment or display
 * boundary. Rounds half-up, same policy as the charge path.
 *
 * Storage stays micro; only the edge converts. Stripe takes MINOR units, so
 * this is what a checkout amount passes through before
 * `assertStripeMinorAmount`.
 */
export function billingMicroToCurrencyMinor(amountBillingMicro: number, currency: string): number {
  assertNonNegativeMonetary(amountBillingMicro, 'amountBillingMicro')
  assertSupportedCurrency(currency)
  const minorPerMajor = currencyMinorUnitsPerMajor(currency)
  const divisor = BigInt(BILLING_MICRO_PER_MAJOR / minorPerMajor)
  if (divisor <= 0n) {
    throw new MonetaryRangeError('currency', `${currency} has an unusable minor-unit exponent`)
  }
  const n = BigInt(amountBillingMicro)
  let quotient = n / divisor
  const remainder = n % divisor
  if (remainder * 2n >= divisor) quotient += 1n
  return Number(quotient)
}
